import { api } from './api/client'

/**
 * PRD-029 §3.3 / §3.4: web push enrollment from the browser. The service worker (`/sw.js`) shows the card and opens
 * the deep link; this module asks permission, subscribes with the instance's VAPID key and posts the subscription.
 * Nothing here holds a secret: the public key is public by definition.
 */
export function pushSupport(): { ok: true } | { ok: false, reason: string } {
  if (typeof window === 'undefined')
    return { ok: false, reason: 'No browser.' }
  if (!('serviceWorker' in navigator))
    return { ok: false, reason: 'This browser cannot run a service worker, so push is not available here.' }
  if (!('PushManager' in window) || !('Notification' in window))
    return { ok: false, reason: 'This browser does not support web push. On an iPhone, add Gallop to the Home Screen first (iOS 16.4 or later).' }
  if (!window.isSecureContext)
    return { ok: false, reason: 'Push needs a secure (https) page.' }
  return { ok: true }
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator))
    return null
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' })
  }
  catch {
    return null
  }
}

export async function enablePushOnThisDevice(vapidPublicKey: string): Promise<{ ok: true, id: string } | { ok: false, reason: string }> {
  const support = pushSupport()
  if (!support.ok)
    return support
  const permission = await Notification.requestPermission()
  if (permission !== 'granted')
    return { ok: false, reason: permission === 'denied' ? 'Notifications are blocked for this site in the browser settings.' : 'Permission was not granted.' }
  const reg = await registerServiceWorker()
  if (!reg)
    return { ok: false, reason: 'The service worker could not be registered (private mode, or an unsupported browser).' }
  await navigator.serviceWorker.ready
  let sub = await reg.pushManager.getSubscription()
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource })
    }
    catch (err) {
      return { ok: false, reason: `The browser refused the subscription: ${err instanceof Error ? err.message : String(err)}` }
    }
  }
  const json = sub.toJSON()
  if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth)
    return { ok: false, reason: 'The browser returned an incomplete subscription.' }
  const r = await api<{ id: string }>('/me/push-subscriptions', { method: 'POST', json: { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth }, userAgent: navigator.userAgent.slice(0, 300) } })
  return { ok: true, id: r.id }
}
